import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { MutableRefObject, forwardRef, useCallback, useEffect } from 'react';

import {
    ContentInsertMode,
    useQuill,
    useSignature,
    useSlashMenu,
} from '@/caregiverSpace/shared/lib';
import { CaregiverPhraseDto } from '@/caregiverSpace/shared/model';

import { SlashMenu } from './SlashMenu';
import './style.css';

type QuillEditorProps = {
    value: string;
    signature?: string;
    placeholder?: string;
    required?: string;
    id?: string;
    onChange: (v: string) => void;
    contentInsertMode?: ContentInsertMode;
    phrases?: CaregiverPhraseDto[];
    onPhraseSelect?: (phrase: CaregiverPhraseDto) => string;
    plainTextOutput?: boolean;
};

export const QuillEditor = forwardRef<Quill | null, QuillEditorProps>(
    (
        {
            placeholder,
            signature,
            value,
            onChange,
            id,
            contentInsertMode,
            phrases,
            onPhraseSelect,
            plainTextOutput,
        },
        forwardedRef,
    ) => {
        const { containerRef, quillRef, lastValueRef, notifyChange } = useQuill(
            {
                placeholder,
                id,
                signature,
                initialValue: value,
                onChange,
                externalRef:
                    forwardedRef as MutableRefObject<Quill | null> | null,
                showPhrasesButton: !!phrases,
                plainTextOutput,
            },
        );

        useSignature({
            quillRef,
            lastValueRef,
            value,
            signature,
            onChange,
            contentInsertMode,
            plainTextOutput,
        });

        const { slashMenuState, handleSelect, handleClose, openViaButton } =
            useSlashMenu({
                quillRef,
                phrases,
                onPhraseSelect,
            });

        const handlePhraseSelect = useCallback(
            (phrase: CaregiverPhraseDto) => {
                handleSelect(phrase);
                notifyChange();
            },
            [handleSelect, notifyChange],
        );

        useEffect(() => {
            if (!containerRef.current) return;
            const btn = containerRef.current.querySelector(
                '.ql-phrases',
            ) as HTMLElement | null;
            if (!btn) return;

            const handler = () => openViaButton(btn);
            btn.addEventListener('click', handler);
            return () => btn.removeEventListener('click', handler);
        }, [containerRef, openViaButton]);

        return (
            <div className="min-h-32">
                <div ref={containerRef} />
                <SlashMenu
                    isOpen={slashMenuState.isOpen}
                    position={slashMenuState.position}
                    phrases={slashMenuState.filteredPhrases}
                    selectedIndex={slashMenuState.selectedIndex}
                    onSelect={handlePhraseSelect}
                    onClose={handleClose}
                />
            </div>
        );
    },
);
